/**
 * Client de l'API de la console.
 *
 * Le jeton de session est opaque et stocké en `localStorage` : révocable côté
 * serveur à tout moment, contrairement à un jeton auto-porté.
 *
 * Le paramètre `environment` accompagne chaque appel qui dépend d'un
 * environnement. Le serveur le revérifie systématiquement : masquer un onglet
 * dans la console n'est pas une protection.
 */

const CLE_JETON = 'infratex.session';

export const jetonStocke = () => localStorage.getItem(CLE_JETON);
export const stockerJeton = (jeton: string) => localStorage.setItem(CLE_JETON, jeton);
export const oublierJeton = () => localStorage.removeItem(CLE_JETON);

/** Erreur portant le code stable de l'API — c'est lui que le support demande. */
export class ErreurApi extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly statut: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

/** Les trois environnements de la console. */
/**
 * Espace de la console — **qui vous êtes**.
 *
 * À distinguer de `EnvMessage`, qui répond à **où vous travaillez**. Les deux
 * axes étaient confondus : le bac à sable figurait dans la même liste que
 * l'espace client, ce qui dupliquait les écrans de clés et de webhooks et
 * laissait sans place le développeur d'un client réel.
 */
export type Espace = 'client' | 'developer' | 'admin';

/**
 * Environnement des messages.
 *
 * Il ne se choisit plus : test et production sont **deux déploiements
 * distincts**, chacun avec ses comptes et ses clés. Un utilisateur ne peut pas
 * se tromper d'environnement s'il n'y en a pas à choisir. Ce type ne sert donc
 * plus qu'à lire ce que le serveur renvoie, jamais à demander quoi que ce soit.
 */
export type EnvMessage = 'live' | 'test';

export interface Plateforme {
  mode: 'test' | 'production';
  /** Le libre-service n'existe que sur la plateforme de test. */
  signup_enabled: boolean;
  key_prefix: string;
}

interface OptionsAppel {
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corps?: unknown;
  parametres?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Télécharge un fichier depuis l'API.
 *
 * Un simple `<a href>` ne peut pas convenir : la session de console
 * s'authentifie par en-tête `Authorization`, qu'un lien n'emporte pas. On
 * récupère donc le contenu par `fetch`, puis on déclenche l'enregistrement.
 */
export async function telecharger(chemin: string, nomFichier: string): Promise<void> {
  const url = new URL(`/console-api${chemin}`, window.location.origin);
  const jeton = jetonStocke();
  const reponse = await fetch(url, {
    headers: jeton ? { Authorization: `Bearer ${jeton}` } : {},
  });

  if (!reponse.ok) {
    const corps = await reponse.json().catch(() => null);
    throw new ErreurApi(
      corps?.error?.code ?? 'internal_error',
      corps?.error?.message ?? `Le téléchargement a échoué (${reponse.status}).`,
      reponse.status,
    );
  }

  const contenu = await reponse.blob();
  const lien = document.createElement('a');
  lien.href = URL.createObjectURL(contenu);
  lien.download = nomFichier;
  lien.click();
  URL.revokeObjectURL(lien.href);
}

async function appeler<T>(chemin: string, options: OptionsAppel = {}): Promise<T> {
  const url = new URL(`/console-api${chemin}`, window.location.origin);

  for (const [cle, valeur] of Object.entries(options.parametres ?? {})) {
    if (valeur !== undefined && valeur !== null && valeur !== '') {
      url.searchParams.set(cle, String(valeur));
    }
  }

  const jeton = jetonStocke();
  const reponse = await fetch(url, {
    method: options.methode ?? 'GET',
    headers: {
      ...(options.corps ? { 'Content-Type': 'application/json' } : {}),
      ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}),
    },
    body: options.corps ? JSON.stringify(options.corps) : undefined,
  });

  if (reponse.status === 204) return undefined as T;

  const donnees = await reponse.json().catch(() => null);

  if (!reponse.ok) {
    const erreur = donnees?.error;
    if (reponse.status === 401) oublierJeton();
    throw new ErreurApi(
      erreur?.code ?? 'internal_error',
      erreur?.message ?? 'Une erreur est survenue.',
      reponse.status,
      erreur?.details,
    );
  }

  return donnees as T;
}

// --- Types du contrat -------------------------------------------------------

export interface Utilisateur {
  id: string;
  email: string;
  full_name: string | null;
  role: 'owner' | 'admin' | 'member';
  is_platform_admin: boolean;
}

export interface Compte {
  id: string;
  name: string;
  status: string;
  tps_limit: number;
  low_balance_threshold_xaf: number;
  sandbox_only: boolean;
}

export interface Identite {
  user: Utilisateur;
  account: Compte;
  /** Espaces ouverts à cette session. */
  spaces: Espace[];
  /** Espace d'arrivée, déduit du rôle. */
  default_space: Espace;
  /**
   * Droits effectifs, calculés par le serveur à partir du rôle et de la
   * casquette d'exploitant. La console s'en sert pour ne pas proposer ce qui
   * sera refusé ; chaque route les revérifie de son côté.
   */
  permissions: string[];
  /** La plateforme servie. Ce n'est pas un choix de l'utilisateur. */
  platform: Plateforme;
}

export type StatutMessage =
  | 'queued'
  | 'submitted'
  | 'sent'
  | 'delivered'
  | 'undelivered'
  | 'expired'
  | 'rejected'
  | 'opted_out';

export interface Message {
  id: string;
  status: StatutMessage;
  to: string;
  from: string;
  body?: string;
  encoding: 'gsm7' | 'ucs2';
  segments: number;
  category: 'transactional' | 'marketing';
  environment: EnvMessage;
  price_per_segment_xaf: number;
  cost_xaf: number;
  client_reference: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  submitted_at: string | null;
  delivered_at: string | null;
  events?: EvenementMessage[];
}

export interface EvenementMessage {
  status: StatutMessage;
  source: string;
  detail: string | null;
  occurred_at: string;
}

export interface Ecriture {
  id: string;
  type: 'topup' | 'message_debit' | 'message_refund' | 'adjustment' | 'reversal';
  amount_xaf: number;
  description: string | null;
  message_id: string | null;
  created_at: string;
}

export interface Apercu {
  balance_xaf: number;
  available_xaf: number;
  low_balance_threshold_xaf: number;
  low_balance: boolean;
  recent_entries: Ecriture[];
  recent_messages: Message[];
}

export interface Estimation {
  encoding: 'gsm7' | 'ucs2';
  characters: number;
  segments: number;
  remaining_in_segment: number;
  non_gsm7_characters: string[];
  price_per_segment_xaf: number;
  unit_cost_xaf: number;
  total_cost_xaf: number;
}

export interface Expediteur {
  id: string;
  value: string;
  status: 'pending' | 'approved' | 'rejected';
  reason: string | null;
  approved_at: string | null;
  created_at: string;
}

export interface CleApi {
  id: string;
  name: string;
  environment: EnvMessage;
  prefix: string;
  last4: string;
  status: 'active' | 'revoked';
  last_used_at: string | null;
  created_at: string;
  /** Présente uniquement à la création. Elle ne sera plus jamais affichée. */
  key?: string;
}

export interface Lot {
  batch_id: string;
  from: string;
  body: string;
  category: string;
  total: number;
  delivered: number;
  failed: number;
  pending: number;
  cost_xaf: number;
  created_at: string;
}

export interface ResultatLot {
  batch_id: string;
  list_id: string | null;
  template_id: string | null;
  accepted: number;
  opted_out: number;
  rejected: { to: string; code: string; message: string }[];
  /** Écartés faute de données pour une variable du modèle. */
  incomplete: { to: string; missing: string[] }[];
  total_segments: number;
  variable_segmentation: boolean;
  total_cost_xaf: number;
}

export interface MoisFacture {
  month: string;
  messages: number;
  segments: number;
  total_ht_xaf: number;
  vat_xaf: number;
  total_ttc_xaf: number;
}

export interface Facturation {
  balance_xaf: number;
  available_xaf: number;
  low_balance_threshold_xaf: number;
  vat_rate_bps: number;
  legal: {
    legal_name: string | null;
    nif: string | null;
    rccm: string | null;
    address: string | null;
  };
  /** Mentions OHADA manquantes : sans elles, aucune facture n'est opposable. */
  missing_mentions: string[];
  months: MoisFacture[];
  entries: Ecriture[];
  invoices: Facture[];
}

export interface PartitionExpiree {
  nom: string;
  parent: string;
  lignes: number;
}

export interface ExecutionPurge {
  id: string;
  status: 'running' | 'succeeded' | 'failed';
  trigger: string;
  triggered_by: string | null;
  cutoff: string;
  dropped_partitions: string[];
  deleted_rows: Record<string, number>;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

export interface Retention {
  retention_months: number;
  cutoff: string;
  /** Tables que la purge ne touche jamais — conservation OHADA de dix ans. */
  preserved_ten_years: string[];
  partitions: PartitionExpiree[];
  rows: Record<string, number>;
  runs: ExecutionPurge[];
}

export interface EntreeAudit {
  id: number;
  action: string;
  actor_type: string;
  actor: string | null;
  account_name: string | null;
  target_type: string | null;
  target_id: string | null;
  metadata: unknown;
  ip_address: string | null;
  created_at: string;
}

export type StatutFacture = 'draft' | 'issued' | 'paid' | 'cancelled';

export interface Facture {
  id: string;
  number: string | null;
  kind: 'invoice' | 'credit_note';
  status: StatutFacture;
  period_start: string;
  period_end: string;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  total_ht_xaf: number;
  total_vat_xaf: number;
  total_ttc_xaf: number;
  vat_rate_bps: number;
  cancel_reason: string | null;
  /** Présents sur les listes de l'administration seulement. */
  account_id?: string;
  account_name?: string;
}

export interface LigneFacture {
  position: number;
  designation: string;
  quantity: number;
  unit: string;
  unit_price_xaf: number;
  total_ht_xaf: number;
  vat_rate_bps: number;
  vat_xaf: number;
}

export interface MentionsEmetteur {
  legal_name: string;
  legal_form: string;
  capital_xaf: number;
  nif: string;
  rccm: string;
  address: string;
  bank: string | null;
}

export interface FactureDetaillee extends Facture {
  issuer: MentionsEmetteur;
  customer: {
    name: string;
    legal_name: string | null;
    nif: string | null;
    rccm: string | null;
    address: string | null;
    contact_email: string | null;
  };
  payment_terms: string | null;
  payment_reference: string | null;
  parent_invoice_id: string | null;
  lines: LigneFacture[];
  missing_mentions?: string[];
}

export type StatutModele = 'draft' | 'pending' | 'approved' | 'rejected';

export interface Modele {
  id: string;
  name: string;
  body: string;
  sender_id: string | null;
  category: 'transactional' | 'marketing';
  status: StatutModele;
  reason: string | null;
  variables: string[];
  template_segments: number;
  usable: boolean;
  submitted_by: string | null;
  approved_by: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  usage_count: number;
  created_at: string;
  updated_at: string;
  /** Renvoyé après une modification qui a ramené le modèle en brouillon. */
  reset_to_draft?: boolean;
}

export interface ApercuModele {
  variables: string[];
  template_segments: number;
  complete_recipients: number;
  incomplete: { to: string; missing: string[] }[];
  incomplete_count: number;
  /** Vrai si les destinataires n'ont pas tous le même nombre de segments. */
  variable_segmentation: boolean;
  best_case: { msisdn: string; corps: string; segments: number } | null;
  worst_case: { msisdn: string; corps: string; segments: number } | null;
  samples: { msisdn: string; corps: string; segments: number }[];
  total_segments: number;
  price_per_segment_xaf: number;
  total_cost_xaf: number;
}

export type BaseLegale = 'contractual' | 'opt_in' | 'paper_form' | 'undocumented';

export interface ListeDiffusion {
  id: string;
  name: string;
  description: string | null;
  legal_basis: BaseLegale;
  legal_basis_label: string;
  /** Cette liste peut-elle porter une campagne publicitaire ? */
  allows_marketing: boolean;
  proof: string | null;
  collected_at: string | null;
  contacts_total: number;
  contacts_opted_out: number;
  contacts_reachable: number;
  created_at: string;
  updated_at: string;
}

export interface ContactListe {
  id: string;
  to: string;
  first_name: string | null;
  attributes: Record<string, string> | null;
  source: string;
  opted_out: boolean;
  created_at: string;
}

export interface ResultatImport {
  listId: string;
  lignesLues: number;
  importes: number;
  dejaPresents: number;
  doublonsFichier: number;
  deja_retires: number;
  rejetees: { ligne: number; valeur: string; motif: string }[];
  consentementsEnregistres: number;
}

export interface Webhook {
  id: string;
  url: string;
  environment: EnvMessage;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  /** Présent uniquement à la création ou après régénération explicite. */
  secret?: string;
}

export interface ConfigWebhook {
  data: Webhook | null;
  max_attempts: number;
  timeout_ms: number;
  retry_schedule_seconds: number[];
}

export interface Livraison {
  id: string;
  event_type: string;
  message_id: string | null;
  status: 'pending' | 'delivered' | 'failed';
  attempts: number;
  last_status_code: number | null;
  last_error: string | null;
  next_attempt_at: string | null;
  delivered_at: string | null;
  created_at: string;
}

export interface Page<T> {
  data: T[];
  next_cursor: string | null;
  has_more: boolean;
}

// --- Administration ---------------------------------------------------------

export interface EtatFile {
  nom: string;
  waiting: number;
  active: number;
  delayed: number;
  failed: number;
}

export interface Supervision {
  gateway: {
    /** Un pilote par opérateur desservi : le routage se fait par préfixe. */
    drivers: {
      operator: string;
      driver: string;
      reaches_operator: boolean;
      available: boolean;
      /** Faux tant que les identifiants de l'opérateur ne sont pas renseignés. */
      configured: boolean;
    }[];
    global_tps_cap: number;
  };
  queues: EtatFile[];
  pending_jobs: number;
  failed_jobs: number;
  stats: {
    active_accounts: number;
    low_balance_accounts: number;
    platform_admins: number;
    pending_sender_ids: number;
    opt_outs_total: number;
    messages_24h: number;
    delivered_24h: number;
    undelivered_24h: number;
    in_flight: number;
    /** Taux de remise en pour mille : 977 = 97,7 %. Entier, jamais un flottant. */
    delivery_rate_per_mille: number | null;
    revenue_24h_xaf: number;
  };
  alerts: { niveau: 'critique' | 'majeure' | 'information'; message: string }[];
}

export interface CompteAdmin {
  id: string;
  name: string;
  legal_name: string | null;
  nif: string | null;
  status: string;
  sandbox_only: boolean;
  tps_limit: number;
  balance_xaf: number;
  low_balance_threshold_xaf: number;
  low_balance: boolean;
  /** Noms d'expéditeur validés, par ordre alphabétique. */
  sender_ids: string[];
  approved_sender_ids: number;
  pending_sender_ids: number;
  active_api_keys: number;
  messages_today: number;
  created_at: string;
}

// --- Comptes clients et utilisateurs ----------------------------------------

/**
 * Le rôle porte les droits. Il n'y a pas d'attribution ligne à ligne : le
 * serveur décrit chaque rôle par la liste de ce qu'il emporte, et c'est cette
 * liste que la console affiche — jamais une liste écrite à la main ici, qui
 * finirait par mentir.
 */
export type RoleClient = 'owner' | 'admin' | 'member' | 'developer';

export interface DescriptionRole {
  role: RoleClient;
  label: string;
  permissions: { key: string; label: string }[];
}

export interface UtilisateurAdmin {
  id: string;
  email: string;
  full_name: string | null;
  role: RoleClient;
  permissions: string[];
  is_active: boolean;
  is_platform_admin: boolean;
  account_id: string;
  account_name: string;
  created_by: string | null;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContexteCompte {
  proprietaires_actifs: number;
  cles_actives: number;
}

export interface DemandeCreationUtilisateur {
  email: string;
  password: string;
  full_name?: string;
  role: RoleClient;
}

export interface DemandeOuvertureCompte {
  company_name: string;
  legal_name?: string;
  nif?: string;
  rccm?: string;
  address?: string;
  contact_email?: string;
  email: string;
  password: string;
  full_name?: string;
  sender_id?: string;
  tps_limit?: number;
  price_per_segment_xaf?: number;
  low_balance_threshold_xaf?: number;
}

export interface CompteOuvert {
  id: string;
  name: string;
  legal_name: string | null;
  nif: string | null;
  rccm: string | null;
  tps_limit: number;
  sandbox_only: boolean;
}

export interface ExpediteurAValider {
  id: string;
  value: string;
  length: number;
  account_name: string;
  anomaly: string | null;
  created_at: string;
}

export interface GrilleTarifaire {
  default_price_per_segment_xaf: number;
  vat_rate_bps: number;
  wholesale_price_per_segment_xaf: number | null;
  margin_note: string;
  data: {
    id: string;
    scope: string;
    account_id: string | null;
    min_segments_month: number;
    price_per_segment_xaf: number;
  }[];
}

/**
 * État tarifaire d'un compte.
 *
 * `effective_price_per_segment_xaf` est le prix que ce compte paie à cette
 * heure, résolu par le serveur. La console ne le recalcule pas : le volume du
 * mois entre dans la résolution du palier, et un prix recomposé ici finirait
 * par différer de celui qui est facturé.
 */
export interface TarifCompte {
  account_id: string;
  account_name: string;
  default_price_per_segment_xaf: number;
  effective_price_per_segment_xaf: number;
  /** D'où vient le prix appliqué : palier négocié, grille commune, ou défaut. */
  price_origin: 'palier_compte' | 'grille_defaut' | 'configuration';
  segments_this_month: number;
  /** Paliers propres au compte, par seuil croissant. Vide : grille par défaut. */
  data: {
    id: string;
    min_segments_month: number;
    price_per_segment_xaf: number;
    created_at: string;
  }[];
}

export interface Registre {
  retention_months: number;
  masked: boolean;
  data: {
    id: string;
    to: string;
    source: string;
    keyword: string | null;
    account_name: string;
    created_at: string;
  }[];
}

// --- Appels -----------------------------------------------------------------

export const api = {
  connexion: (email: string, password: string) =>
    appeler<{ token: string; user: Utilisateur; account: Compte }>('/login', {
      methode: 'POST',
      corps: { email, password },
    }),

  deconnexion: () => appeler<void>('/logout', { methode: 'POST' }),

  moi: () => appeler<Identite>('/me'),

  apercu: () => appeler<Apercu>('/overview'),

  estimer: (body: string, recipients: number) =>
    appeler<Estimation>('/estimate', {
      methode: 'POST',
      corps: { body, recipients },
    }),

  envoyer: (message: { to: string; from: string; body: string; category?: string }) =>
    appeler<Message>('/messages', { methode: 'POST', corps: message }),

  envoyerLot: (lot: {
    to?: string[];
    list_id?: string;
    template_id?: string;
    from: string;
    body?: string;
    category?: string;
  }) => appeler<ResultatLot>('/messages/batch', { methode: 'POST', corps: lot }),

  lots: () => appeler<{ data: Lot[] }>('/batches'),

  journal: (
    parametres: {
      status?: string;
      to?: string;
      cursor?: string | null;
      limit?: number;
    } = {},
  ) => appeler<Page<Message>>('/messages', { parametres }),

  message: (id: string) => appeler<Message>(`/messages/${id}`),

  /** Description publique de la plateforme. Sans authentification. */
  plateforme: () => appeler<Plateforme>('/platform'),

  inscription: (demande: {
    company_name: string;
    email: string;
    password: string;
    full_name?: string;
    sender_id?: string;
  }) =>
    appeler<{
      token: string;
      user: { id: string; email: string; role: string };
      account: { id: string; name: string };
      platform: Plateforme;
    }>('/signup', { methode: 'POST', corps: demande }),

  facturation: () => appeler<Facturation>('/billing'),

  facture: (id: string) => appeler<FactureDetaillee>(`/invoices/${id}`),

  expediteurs: () => appeler<{ data: Expediteur[] }>('/sender-ids'),

  creerExpediteur: (value: string) =>
    appeler<Expediteur>('/sender-ids', { methode: 'POST', corps: { value } }),

  modeles: () => appeler<{ data: Modele[]; can_approve: boolean }>('/templates'),

  creerModele: (m: { name: string; body: string; sender_id?: string | null; category?: string }) =>
    appeler<Modele>('/templates', { methode: 'POST', corps: m }),

  modifierModele: (
    id: string,
    m: { name: string; body: string; sender_id?: string | null; category?: string },
  ) => appeler<Modele>(`/templates/${id}`, { methode: 'PUT', corps: m }),

  statuerModele: (
    id: string,
    decision: 'submit' | 'approve' | 'reject' | 'withdraw',
    reason?: string,
  ) => appeler<Modele>(`/templates/${id}/status`, { methode: 'POST', corps: { decision, reason } }),

  supprimerModele: (id: string) => appeler<void>(`/templates/${id}`, { methode: 'DELETE' }),

  apercuModele: (id: string, listId: string) =>
    appeler<ApercuModele>(`/templates/${id}/preview`, {
      methode: 'POST',
      corps: { list_id: listId },
    }),

  listes: () => appeler<{ data: ListeDiffusion[] }>('/lists'),

  creerListe: (liste: {
    name: string;
    description?: string | null;
    legal_basis: BaseLegale;
    proof?: string | null;
    collected_at?: string | null;
  }) => appeler<ListeDiffusion>('/lists', { methode: 'POST', corps: liste }),

  modifierListe: (
    id: string,
    liste: {
      name: string;
      description?: string | null;
      legal_basis: BaseLegale;
      proof?: string | null;
      collected_at?: string | null;
    },
  ) => appeler<ListeDiffusion>(`/lists/${id}`, { methode: 'PUT', corps: liste }),

  supprimerListe: (id: string) => appeler<void>(`/lists/${id}`, { methode: 'DELETE' }),

  importer: (id: string, csv: string) =>
    appeler<ResultatImport>(`/lists/${id}/import`, { methode: 'POST', corps: { csv } }),

  contactsDeLaListe: (id: string, recherche?: string) =>
    appeler<{ total: number; data: ContactListe[] }>(`/lists/${id}/contacts`, {
      parametres: { q: recherche, limit: 200 },
    }),

  supprimerContact: (id: string) => appeler<void>(`/contacts/${id}`, { methode: 'DELETE' }),

  webhook: () => appeler<ConfigWebhook>('/webhooks'),

  definirWebhook: (url: string, enabled: boolean, rotate = false) =>
    appeler<Webhook>('/webhooks', {
      methode: 'PUT',
      corps: { url, enabled },
      parametres: rotate ? { rotate_secret: true } : {},
    }),

  livraisons: () => appeler<{ data: Livraison[] }>('/webhooks/deliveries'),

  clesApi: () => appeler<{ data: CleApi[] }>('/api-keys'),

  creerCleApi: (name: string) => appeler<CleApi>('/api-keys', { methode: 'POST', corps: { name } }),

  revoquerCleApi: (id: string) => appeler<void>(`/api-keys/${id}`, { methode: 'DELETE' }),

  // --- Équipe du compte -----------------------------------------------------
  //
  // Le client gère ses propres utilisateurs, comme il l'a fait à l'inscription
  // sur la plateforme de test. L'exploitant n'ajoute personne ici.

  equipe: {
    lister: () =>
      appeler<{ data: UtilisateurAdmin[]; context: ContexteCompte; roles: DescriptionRole[] }>(
        '/users',
      ),

    creer: (corps: DemandeCreationUtilisateur) =>
      appeler<{ user: UtilisateurAdmin; note: string }>('/users', { methode: 'POST', corps }),

    modifier: (
      id: string,
      corps: Partial<{ role: RoleClient; is_active: boolean; full_name: string | null }>,
    ) =>
      appeler<{ user: UtilisateurAdmin; revoked_sessions: number }>(`/users/${id}`, {
        methode: 'PATCH',
        corps,
      }),

    motDePasse: (id: string, motDePasse: string) =>
      appeler<{ user: UtilisateurAdmin; revoked_sessions: number; note: string }>(
        `/users/${id}/password`,
        { methode: 'POST', corps: { password: motDePasse } },
      ),
  },

  admin: {
    // --- Facturation, côté administration -------------------------------------

    factures: (annee: number) =>
      appeler<{ year: number; numbering_gaps: number[]; data: Facture[] }>(
        `/admin/invoices?year=${annee}`,
      ),

    brouillonFacture: (accountId: string, annee: number, mois: number) =>
      appeler<FactureDetaillee & { account_name: string }>('/admin/invoices/draft', {
        methode: 'POST',
        corps: { account_id: accountId, year: annee, month: mois },
      }),

    emettreFacture: (id: string) =>
      appeler<FactureDetaillee>(`/admin/invoices/${id}/issue`, { methode: 'POST' }),

    reglerFacture: (id: string, reference: string) =>
      appeler<Facture>(`/admin/invoices/${id}/pay`, {
        methode: 'POST',
        corps: { payment_reference: reference },
      }),

    annulerFacture: (id: string, motif: string) =>
      appeler<{ invoice: Facture; credit_note: Facture }>(`/admin/invoices/${id}/cancel`, {
        methode: 'POST',
        corps: { reason: motif },
      }),

    recharger: (corps: {
      account_id: string;
      amount_xaf: number;
      payment_reference: string;
      payment_method: string;
      description?: string;
    }) =>
      appeler<{
        applied: boolean;
        duplicate: boolean;
        account_name: string;
        balance_xaf: number;
        available_xaf: number;
      }>('/admin/top-ups', { methode: 'POST', corps }),

    seuilAlerte: (accountId: string, seuil: number) =>
      appeler<{ id: string; name: string; low_balance_threshold_xaf: number }>(
        `/admin/accounts/${accountId}/threshold`,
        { methode: 'POST', corps: { low_balance_threshold_xaf: seuil } },
      ),

    supervision: () => appeler<Supervision>('/admin/overview'),

    // --- Conformité ---------------------------------------------------------

    retention: () => appeler<Retention>('/admin/compliance/retention'),

    purger: (simulation: boolean) =>
      appeler<{
        dry_run: boolean;
        cutoff: string;
        dropped_partitions: string[];
        deleted_rows: Record<string, number>;
      }>('/admin/compliance/retention/purge', {
        methode: 'POST',
        corps: { dry_run: simulation },
      }),

    audit: (limite = 100) =>
      appeler<{ retention_months: number; data: EntreeAudit[] }>(
        `/admin/compliance/audit?limit=${limite}`,
      ),
    comptes: () => appeler<{ data: CompteAdmin[] }>('/admin/accounts'),
    expediteurs: () => appeler<{ data: ExpediteurAValider[] }>('/admin/sender-ids'),
    statuerExpediteur: (id: string, decision: 'approve' | 'reject', reason?: string) =>
      appeler<Expediteur>(`/admin/sender-ids/${id}`, {
        methode: 'POST',
        corps: { decision, reason },
      }),
    tarifs: () => appeler<GrilleTarifaire>('/admin/pricing'),

    /** Expéditeurs d'un compte, tous statuts confondus — avec leur identifiant. */
    expediteursDuCompte: (accountId: string) =>
      appeler<{ account_id: string; account_name: string; data: Expediteur[] }>(
        `/admin/accounts/${accountId}/sender-ids`,
      ),

    /**
     * Supprime un expéditeur. Irréversible : elle libère le nom pour ce compte
     * comme pour la plateforme, là où un refus le garde pris.
     */
    supprimerExpediteur: (id: string) =>
      appeler<{ deleted: true; id: string; value: string; previous_status: string }>(
        `/admin/sender-ids/${id}`,
        { methode: 'DELETE' },
      ),

    /**
     * Tarif d'un compte. Les trois appels rendent le même état complet : la
     * console n'a jamais à recharger après un geste, ni à recomposer le prix.
     */
    tarifCompte: (accountId: string) =>
      appeler<TarifCompte>(`/admin/accounts/${accountId}/pricing`),

    definirTarif: (
      accountId: string,
      corps: { min_segments_month: number; price_per_segment_xaf: number },
    ) => appeler<TarifCompte>(`/admin/accounts/${accountId}/pricing`, { methode: 'PUT', corps }),

    retirerPalier: (accountId: string, tierId: string) =>
      appeler<TarifCompte>(`/admin/accounts/${accountId}/pricing/${tierId}`, {
        methode: 'DELETE',
      }),
    registre: (reveal = false) => appeler<Registre>('/admin/opt-outs', { parametres: { reveal } }),

    // --- Comptes clients et utilisateurs -------------------------------------
    //
    // En production, le client ne s'inscrit pas : INFRATEX ouvre son compte
    // après intégration, puis crée son équipe. C'est ce qui empêche qu'un essai
    // devienne un envoi réel.

    ouvrirCompte: (corps: DemandeOuvertureCompte) =>
      appeler<{ account: CompteOuvert; user: { id: string; email: string; role: RoleClient } }>(
        '/admin/accounts',
        { methode: 'POST', corps },
      ),

    roles: () => appeler<{ data: DescriptionRole[] }>('/admin/roles'),

    /** L'équipe d'un compte client, en lecture — pour le support. */
    utilisateurs: (accountId: string) =>
      appeler<{ data: UtilisateurAdmin[]; context: ContexteCompte }>(
        `/admin/accounts/${accountId}/users`,
      ),

    /** L'équipe de l'exploitant : ceux qui administrent la plateforme. */
    utilisateursPlateforme: () => appeler<{ data: UtilisateurAdmin[] }>('/admin/platform-users'),

    /**
     * Crée un utilisateur **de la plateforme**. Ni compte ni casquette dans le
     * corps : le serveur les décide. Les utilisateurs d'un client sont créés
     * par le client, depuis son espace.
     */
    creerUtilisateurPlateforme: (corps: DemandeCreationUtilisateur) =>
      appeler<{ user: UtilisateurAdmin; note: string }>('/admin/platform-users', {
        methode: 'POST',
        corps,
      }),

    /**
     * Modification partielle : seuls les champs envoyés changent. Renvoyer
     * l'objet entier écraserait ce qu'un autre administrateur vient de modifier.
     */
    modifierUtilisateur: (
      id: string,
      corps: Partial<{
        role: RoleClient;
        is_active: boolean;
        is_platform_admin: boolean;
        full_name: string | null;
      }>,
    ) =>
      appeler<{ user: UtilisateurAdmin; revoked_sessions: number }>(`/admin/users/${id}`, {
        methode: 'PATCH',
        corps,
      }),

    motDePasseUtilisateur: (id: string, motDePasse: string) =>
      appeler<{ user: UtilisateurAdmin; revoked_sessions: number; note: string }>(
        `/admin/users/${id}/password`,
        { methode: 'POST', corps: { password: motDePasse } },
      ),
  },
};
